import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { submitFoundation } from "@/lib/api";

const intents = [
  { id: "donate", label: "Donate", desc: "Support the mission with a one-time or recurring gift." },
  { id: "sponsor-veteran", label: "Sponsor a Veteran", desc: "Fund a veteran through the program." },
  { id: "sponsor-dog", label: "Sponsor a Service Dog", desc: "Fund the training of a service dog." },
  { id: "partner", label: "Partner With Golden Wolf", desc: "Corporate, community or nonprofit collaboration." },
  { id: "volunteer", label: "Volunteer", desc: "Contribute your time, skills or facility." },
  { id: "apply", label: "Apply for a Program", desc: "Veteran applying for training or workforce pathway." },
  { id: "facility", label: "Support the Facility Mission", desc: "Capital campaign for the physical facility." },
];

const amounts = ["$50", "$100", "$250", "$500", "$1,000", "$2,500", "$5,000+", "Other / discuss"];

export default function GoldenWolf() {
  const [selected, setSelected] = useState("donate");
  const [form, setForm] = useState({ name: "", email: "", phone: "", intent: "donate", organization: "", amount: "", message: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { document.title = "Golden Wolf Foundation — Veterans, Service Dogs, Purpose"; window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await submitFoundation({ ...form, intent: selected });
      toast.success("Received. The Golden Wolf team will be in touch shortly.");
      setForm({ ...form, name: "", email: "", phone: "", organization: "", amount: "", message: "" });
    } catch { toast.error("Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div className="pt-[120px] md:pt-[140px]" data-testid="golden-wolf-page">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 relative overflow-hidden">
        <div className="blob" style={{ width: 500, height: 500, top: "-100px", right: "-200px", background: "radial-gradient(circle, rgba(15,93,58,0.25), transparent 70%)" }}/>
        <div className="text-eyebrow text-gold mb-4">The Golden Wolf Foundation</div>
        <h1 className="font-display text-[clamp(2.2rem,7vw,7rem)] leading-[0.92] tracking-[-0.03em] max-w-5xl">
          Healing veterans. Training dogs.<br/><span className="italic gold-foil">Rebuilding purpose.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-bone/75 font-editorial text-lg">
          The Golden Wolf Foundation supports veterans, develops service-dog training programs, creates workforce pathways, and builds partnerships among veterans, dogs, businesses and communities.
        </p>
      </section>

      {/* Mission grid */}
      <section className="section pt-0 bg-ink-2">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="text-eyebrow text-gold mb-8">The Mission</div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              ["Support veterans", "Mental-health, service dogs and community."],
              ["Service-dog training", "Purpose-built partners for PTSD, mobility, medical alert."],
              ["Workforce pathways", "Train veterans to become professional dog trainers."],
              ["Community partnerships", "Bridge veterans, dogs, businesses and neighborhoods."],
              ["Mental-health support", "Structure, purpose and human-canine connection."],
              ["Facility mission", "Building a permanent Chicago home for the work."],
            ].map(([t, d], i) => (
              <motion.div key={t}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.05 * i }}
                className="p-6 border border-emerald bg-ink"
              >
                <div className="text-hair text-emerald mb-2">0{i + 1}</div>
                <div className="font-display italic text-2xl">{t}</div>
                <p className="mt-3 text-bone/70 text-sm">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Intent picker */}
      <section className="section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="text-eyebrow text-gold mb-4">How you can help</div>
          <h2 className="font-display text-4xl md:text-6xl leading-tight mb-10">Choose your <span className="italic gold-foil">contribution.</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-16">
            {intents.map((it, i) => (
              <motion.button key={it.id}
                onClick={() => { setSelected(it.id); setForm({ ...form, intent: it.id }); setTimeout(() => document.getElementById("gw-form")?.scrollIntoView({ behavior: "smooth" }), 200); }}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.04 * i }}
                className={`text-left p-6 border ${selected === it.id ? "border-gold bg-gradient-to-b from-gold/5 to-transparent" : "border-hair hover:border-gold/60"}`}
                data-testid={`gw-intent-${it.id}`}
              >
                <div className={`text-hair mb-2 ${selected === it.id ? "text-gold" : "text-bone/50"}`}>0{i + 1}</div>
                <div className="font-display italic text-xl">{it.label}</div>
                <p className="mt-2 text-sm text-bone/60">{it.desc}</p>
              </motion.button>
            ))}
          </div>

          {/* Form */}
          <form id="gw-form" onSubmit={submit} className="grid md:grid-cols-12 gap-10 border-t border-hair pt-16" data-testid="gw-form">
            <div className="md:col-span-5">
              <div className="text-eyebrow text-gold">Contribute</div>
              <h3 className="font-display text-4xl md:text-5xl mt-4 leading-tight">Join the <span className="italic gold-foil">pack.</span></h3>
              <p className="mt-6 text-bone/70 font-editorial">Note: this is a nonprofit contact form. Donations are handled separately from commercial dog-training services.</p>
            </div>
            <div className="md:col-span-7 grid md:grid-cols-2 gap-6">
              <div className="field"><label>Name</label><input required value={form.name} onChange={upd("name")} data-testid="gw-name"/></div>
              <div className="field"><label>Email</label><input required type="email" value={form.email} onChange={upd("email")} data-testid="gw-email"/></div>
              <div className="field"><label>Phone</label><input value={form.phone} onChange={upd("phone")} data-testid="gw-phone"/></div>
              <div className="field"><label>Organization</label><input value={form.organization} onChange={upd("organization")} data-testid="gw-org"/></div>
              {(selected === "donate" || selected === "sponsor-veteran" || selected === "sponsor-dog" || selected === "facility") && (
                <div className="field md:col-span-2"><label>Contribution amount</label>
                  <select value={form.amount} onChange={upd("amount")} data-testid="gw-amount">
                    <option value="">Select</option>
                    {amounts.map(a => <option key={a}>{a}</option>)}
                  </select>
                </div>
              )}
              <div className="field md:col-span-2"><label>Message</label><textarea value={form.message} onChange={upd("message")} data-testid="gw-message"/></div>
              <div className="md:col-span-2">
                <button type="submit" disabled={loading} className="btn-gold" data-testid="gw-submit">
                  {loading ? "Sending…" : "Send"}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
